import React, { useState, useEffect } from 'react'
import { useDispatch } from 'react-redux'
import { useNavigate, useLocation } from 'react-router-dom'
import { EyeIcon, EyeSlashIcon, UserIcon, LockClosedIcon, InformationCircleIcon } from '@heroicons/react/24/outline'
import { AppDispatch } from '../../store'
import { login, register } from '../../store/slices/authSlice'
import { useForm } from 'react-hook-form'
import CytestLink from '../../components/CytestLink'
import toast from 'react-hot-toast'
import { registrationErrorMessage } from '../../utils/registrationErrors'

interface LoginForm {
  email: string
  password: string
}

interface RegisterForm {
  email: string
  password: string
  confirmPassword: string
  first_name: string
  last_name: string
  phone_number?: string
  role: 'candidate' | 'recruiter'
}

const AuthPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>()
  const navigate = useNavigate()
  const location = useLocation()
  const [activeTab, setActiveTab] = useState<'login' | 'signup'>('login')

  // Set active tab based on current route
  useEffect(() => {
    if (location.pathname === '/register') {
      setActiveTab('signup')
    } else {
      setActiveTab('login')
    }
  }, [location.pathname])
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  // Login form
  const { register: registerLogin, handleSubmit: handleLoginSubmit, formState: { errors: loginErrors } } = useForm<LoginForm>()
  
  // Register form
  const { register: registerRegister, handleSubmit: handleRegisterSubmit, watch, formState: { errors: registerErrors } } = useForm<RegisterForm>({
    defaultValues: {
      role: 'candidate'
    }
  })

  const password = watch('password')

  const handleForgotPassword = () => {
    navigate('/forgot-password')
  }

  const onLoginSubmit = async (data: LoginForm) => {
    try {
      setIsLoading(true)
      const result = await dispatch(login(data))
      
      if (result.type === 'auth/login/fulfilled') {
        toast.success('Login successful!')
        navigate('/dashboard')
      } else {
        toast.error('Login failed. Please check your credentials.')
      }
    } catch (error) {
      toast.error('An error occurred during login')
    } finally {
      setIsLoading(false)
    }
  }

  const onRegisterSubmit = async (data: RegisterForm) => {
    try {
      setIsLoading(true)
      
      // Remove confirmPassword from the data
      const { confirmPassword, ...registrationData } = data
      
      const result = await dispatch(register(registrationData))
      
      if (result.type === 'auth/register/fulfilled') {
        toast.success('Registration successful! Please log in.')
        setActiveTab('login')
      } else {
        // Show more specific error message
        const errorMessage = registrationErrorMessage(result.payload)
        toast.error(errorMessage)
      }
    } catch (error: any) {
      const errorMessage = registrationErrorMessage(error)
      toast.error(errorMessage)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Main Content */}
      <div className="flex-1 flex items-start justify-center pt-8 pb-0 px-4 sm:px-6 lg:px-8" style={{ minHeight: 'calc(100vh - 1px)' }}>
        <div className="max-w-xl w-full">
          {/* Logo and Branding */}
          <div className="text-center mb-4">
            <CytestLink 
              className="text-5xl font-black bg-gradient-to-r from-violet-600 to-purple-600 bg-clip-text text-transparent" 
              showAsLink={false}
            />
            <p className="mt-2 text-base text-gray-600 font-medium">
             Connecting Talent with Opportunity
            </p>
          </div>

          {/* Auth Form Container */}
          <div className="bg-white rounded-xl shadow-lg border border-gray-200 px-12 py-6">
            {/* Tabs */}
            <div className="flex mb-5 border-b border-gray-200">
              <button
                type="button"
                onClick={() => {
                  setActiveTab('login')
                  navigate('/login')
                }}
                className={`flex-1 py-3 px-6 text-base font-semibold text-center border-b-2 transition-all duration-200 ${
                  activeTab === 'login'
                    ? 'text-blue-600 border-blue-600'
                    : 'text-gray-500 border-transparent hover:text-gray-700'
                }`}
              >
                LOGIN
              </button>
              <button
                type="button"
                onClick={() => {
                  setActiveTab('signup')
                  navigate('/register')
                }}
                className={`flex-1 py-3 px-6 text-base font-semibold text-center border-b-2 transition-all duration-200 ${
                  activeTab === 'signup'
                    ? 'text-blue-600 border-blue-600'
                    : 'text-gray-500 border-transparent hover:text-gray-700'
                }`}
              >
                SIGNUP
              </button>
            </div>

            {/* Information Message */}
            <div className="flex items-center mb-5 p-3 bg-blue-50 rounded-lg border border-blue-100">
              <InformationCircleIcon className="h-5 w-5 text-blue-600 mr-3 flex-shrink-0" />
              <span className="text-sm text-gray-700">
                {activeTab === 'login' 
                  ? "Don't have a " 
                  : "Already have a "
                }
                <CytestLink className="inline" showAsLink={false} />
                {activeTab === 'login' 
                  ? " account? " 
                  : " account? "
                }
                <button
                  type="button"
                  onClick={() => {
                    const newTab = activeTab === 'login' ? 'signup' : 'login'
                    setActiveTab(newTab)
                    navigate(newTab === 'login' ? '/login' : '/register')
                  }}
                  className="font-semibold text-blue-600 hover:text-blue-700 underline"
                >
                  {activeTab === 'login' ? 'Sign up' : 'Sign in'}
                </button>
              </span>
            </div>

            {/* Login Form */}
            {activeTab === 'login' && (
              <form onSubmit={handleLoginSubmit(onLoginSubmit)} className="space-y-4 pb-20">
                <div>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                      <UserIcon className="h-5 w-5 text-gray-800" />
                    </div>
                    <input
                      {...registerLogin('email', {
                        required: 'Email / Username is required',
                        validate: (value) => {
                          // Allow "Administrator" as a special username
                          if (value === 'Administrator') {
                            return true
                          }
                          // Otherwise validate as email
                          const emailPattern = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i
                          return emailPattern.test(value) || 'Please enter a valid email address'
                        }
                      })}
                      type="text"
                      autoComplete="username"
                      placeholder="Email / Username"
                      className="w-full pl-12 pr-4 py-4 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                    />
                  </div>
                  {loginErrors.email && (
                    <p className="mt-1 text-sm text-red-600">{loginErrors.email.message}</p>
                  )}
                </div>

                <div>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                      <LockClosedIcon className="h-5 w-5 text-gray-800" />
                    </div>
                    <input
                      {...registerLogin('password', {
                        required: 'Password is required',
                        minLength: {
                          value: 6,
                          message: 'Password must be at least 6 characters'
                        }
                      })}
                      type={showPassword ? 'text' : 'password'}
                      autoComplete="current-password"
                      placeholder="Password"
                      className="w-full pl-12 pr-12 py-4 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                    />
                    <button
                      type="button"
                      className="absolute inset-y-0 right-0 pr-4 flex items-center"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? (
                        <EyeSlashIcon className="h-5 w-5 text-gray-600 hover:text-gray-800" />
                      ) : (
                        <EyeIcon className="h-5 w-5 text-gray-600 hover:text-gray-800" />
                      )}
                    </button>
                  </div>
                  {loginErrors.password && (
                    <p className="mt-1 text-sm text-red-600">{loginErrors.password.message}</p>
                  )}
                </div>

                <div className="pt-6">
                  <button
                    type="submit"
                    disabled={isLoading}
                    className="w-full bg-blue-600 text-white py-4 px-6 rounded-lg text-base font-semibold hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                  {isLoading ? (
                    <div className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                      LOGGING IN...
                    </div>
                  ) : (
                    'LOG IN'
                  )}
                  </button>
                </div>

                <div className="text-center mt-3">
                  <button
                    type="button"
                    onClick={handleForgotPassword}
                    className="text-sm font-medium text-blue-600 hover:text-blue-700 hover:underline"
                  >
                    Reset your password
                  </button>
                </div>
              </form>
            )}

            {/* Register Form */}
            {activeTab === 'signup' && (
              <form onSubmit={handleRegisterSubmit(onRegisterSubmit)} className="space-y-4">
                {/* Hidden Role Field - Always Candidate for Public Signup */}
                <input
                  {...registerRegister('role')}
                  type="hidden"
                  value="candidate"
                />

                {/* Name Fields */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <input
                      {...registerRegister('first_name', {
                        required: 'First name is required',
                        minLength: {
                          value: 2,
                          message: 'First name must be at least 2 characters'
                        }
                      })}
                      type="text"
                      autoComplete="given-name"
                      placeholder="First name"
                      className="w-full px-4 py-4 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                    />
                    {registerErrors.first_name && (
                      <p className="mt-2 text-sm text-red-600">{registerErrors.first_name.message}</p>
                    )}
                  </div>
                  
                  <div>
                    <input
                      {...registerRegister('last_name', {
                        required: 'Last name is required',
                        minLength: {
                          value: 2,
                          message: 'Last name must be at least 2 characters'
                        }
                      })}
                      type="text"
                      placeholder="Last name"
                      className="w-full px-4 py-4 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                    />
                    {registerErrors.last_name && (
                      <p className="mt-2 text-sm text-red-600">{registerErrors.last_name.message}</p>
                    )}
                  </div>
                </div>

                {/* Email */}
                <div>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                      <UserIcon className="h-5 w-5 text-gray-800" />
                    </div>
                    <input
                      {...registerRegister('email', {
                        required: 'Email is required',
                        pattern: {
                          value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                          message: 'Invalid email address'
                        }
                      })}
                      type="email"
                      autoComplete="email"
                      placeholder="Email address"
                      className="w-full pl-12 pr-4 py-4 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                    />
                  </div>
                  {registerErrors.email && (
                    <p className="mt-2 text-sm text-red-600">{registerErrors.email.message}</p>
                  )}
                </div>

                {/* Password */}
                <div>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                      <LockClosedIcon className="h-5 w-5 text-gray-800" />
                    </div>
                    <input
                      {...registerRegister('password', {
                        required: 'Password is required',
                        minLength: {
                          value: 8,
                          message: 'Password must be at least 8 characters'
                        },
                        pattern: {
                          value: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
                          message: 'Password must contain at least one uppercase letter, lowercase letter, and number'
                        }
                      })}
                      type={showPassword ? 'text' : 'password'}
                      autoComplete="new-password"
                      placeholder="Create a password"
                      className="w-full pl-12 pr-12 py-4 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                    />
                    <button
                      type="button"
                      className="absolute inset-y-0 right-0 pr-4 flex items-center"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? (
                        <EyeSlashIcon className="h-5 w-5 text-gray-600 hover:text-gray-800" />
                      ) : (
                        <EyeIcon className="h-5 w-5 text-gray-600 hover:text-gray-800" />
                      )}
                    </button>
                  </div>
                  {registerErrors.password && (
                    <p className="mt-2 text-sm text-red-600">{registerErrors.password.message}</p>
                  )}
                </div>

                {/* Confirm Password */}
                <div>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                      <LockClosedIcon className="h-5 w-5 text-gray-800" />
                    </div>
                    <input
                      {...registerRegister('confirmPassword', {
                        required: 'Please confirm your password',
                        validate: value => value === password || 'Passwords do not match'
                      })}
                      type={showConfirmPassword ? 'text' : 'password'}
                      autoComplete="new-password"
                      placeholder="Confirm password"
                      className="w-full pl-12 pr-12 py-4 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                    />
                    <button
                      type="button"
                      className="absolute inset-y-0 right-0 pr-4 flex items-center"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    >
                      {showConfirmPassword ? (
                        <EyeSlashIcon className="h-5 w-5 text-gray-600 hover:text-gray-800" />
                      ) : (
                        <EyeIcon className="h-5 w-5 text-gray-600 hover:text-gray-800" />
                      )}
                    </button>
                  </div>
                  {registerErrors.confirmPassword && (
                    <p className="mt-2 text-sm text-red-600">{registerErrors.confirmPassword.message}</p>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full bg-blue-600 text-white py-4 px-6 rounded-lg text-base font-semibold hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed mt-4"
                >
                  {isLoading ? (
                    <div className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                      CREATING ACCOUNT...
                    </div>
                  ) : (
                    'CREATE ACCOUNT'
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-gray-100 border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            {/* Branding and Copyright */}
            <div className="md:col-span-1">
              <div className="flex items-center mb-2">
                <CytestLink 
                  className="text-lg font-black bg-gradient-to-r from-violet-600 to-purple-600 bg-clip-text text-transparent" 
                  showAsLink={false}
                />
              </div>
              <p className="text-sm text-gray-600 mb-2">© 2025. All rights reserved.</p>
              <p className="text-sm text-gray-600 mb-2">Cyfuture</p>
              </div>
            

            {/* For Employers */}
            <div>
              <h3 className="text-sm font-semibold text-gray-900 mb-2">For Employers</h3>
              <ul className="space-y-1">
                <li><a href="#" className="text-sm text-blue-600 hover:text-blue-500">Features</a></li>
                <li><a href="#" className="text-sm text-blue-600 hover:text-blue-500">Technologies</a></li>
                <li><a href="#" className="text-sm text-blue-600 hover:text-blue-500">Pricing</a></li>
                <li><a href="#" className="text-sm text-blue-600 hover:text-blue-500">Request Demo</a></li>
                <li><a href="#" className="text-sm text-blue-600 hover:text-blue-500">Contact Sales</a></li>
              </ul>
            </div>

            {/* For Developers */}
            <div>
              <h3 className="text-sm font-semibold text-gray-900 mb-2">For Developers</h3>
              <ul className="space-y-1">
                <li><a href="#" className="text-sm text-blue-600 hover:text-blue-500">Create profile</a></li>
                <li><a href="#" className="text-sm text-blue-600 hover:text-blue-500">Documentation</a></li>
              </ul>
            </div>

            {/* Company */}
            <div>
              <h3 className="text-sm font-semibold text-gray-900 mb-2">Company</h3>
              <ul className="space-y-1">
                <li><a href="#" className="text-sm text-blue-600 hover:text-blue-500">About</a></li>
                <li><a href="#" className="text-sm text-blue-600 hover:text-blue-500">Jobs</a></li>
                <li><a href="#" className="text-sm text-blue-600 hover:text-blue-500">Blog</a></li>
                <li><a href="#" className="text-sm text-blue-600 hover:text-blue-500">Press Kit</a></li>
                <li><a href="#" className="text-sm text-blue-600 hover:text-blue-500">CSR Policy</a></li>
                <li><a href="#" className="text-sm text-blue-600 hover:text-blue-500">Scheme of Amalgamation</a></li>
              </ul>
            </div>

            {/* Resources */}
            <div>
              <h3 className="text-sm font-semibold text-gray-900 mb-2">Resources</h3>
              <ul className="space-y-1">
                <li><a href="#" className="text-sm text-blue-600 hover:text-blue-500">Privacy</a></li>
                <li><a href="#" className="text-sm text-blue-600 hover:text-blue-500">Terms of Service</a></li>
                <li><a href="#" className="text-sm text-blue-600 hover:text-blue-500">Cookie Policy</a></li>
                <li><a href="#" className="text-sm text-blue-600 hover:text-blue-500">Help Center</a></li>
                <li><a href="#" className="text-sm text-blue-600 hover:text-blue-500">Contact</a></li>
              </ul>
            </div>
          </div>
        </div>
      </footer>
    </div>
  )
}

export default AuthPage
